import React from 'react';
import { Spin, Card } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import BackButton from '../../components/BackButton';
import ServiceProviderOnboardingWizard from '../../components/ServiceProviderOnboardingWizard';
import { useServiceProviderProfile } from '../../contextProviders/ServiceProviderProfileProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

/**
 * The one and only place ServiceProviderOnboardingWizard is ever shown --
 * always a full page, never a modal, same convention as HostOnboardingPage.
 * Unlike that page (which redirects away entirely once hasHostProfile is
 * true, since publishing a Machinery is the only thing left to do), this one
 * stays reachable even with an existing ServiceProviderProfile: the wizard
 * itself skips straight to its 'payments' step in that case (see its own
 * existingProfile prop), so revisiting this page is exactly how a provider
 * who created a profile but skipped Stripe comes back to finish that step --
 * there is no separate Settings section for that yet the way HostProfile has
 * PayoutsTab.
 */
const ServiceProviderOnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { serviceProviderProfile, loading, refresh } = useServiceProviderProfile();

  if (loading) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 700 }}>
        <div style={{ marginBottom: 8 }}>
          <BackButton fallbackPath="/heavy-duty-machinery/services" />
        </div>
        <h2 style={{ margin: '0 0 8px' }}>{translate(t, TRANSLATIONS.SERVICE_PROVIDER_ONBOARDING_TITLE)}</h2>
        <p style={{ marginTop: 0, marginBottom: 20, color: 'rgba(0,0,0,0.55)' }}>
          {translate(t, TRANSLATIONS.SERVICE_PROVIDER_ONBOARDING_INTRO)}
        </p>
        <Card style={{ borderRadius: 20, border: '0.5px solid rgba(0,0,0,0.08)', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
          <ServiceProviderOnboardingWizard
            existingProfile={serviceProviderProfile}
            onComplete={async () => {
              await refresh();
              navigate('/heavy-duty-machinery/services');
            }}
            onCancel={() => navigate('/heavy-duty-machinery/services')}
          />
        </Card>
      </div>
    </AppLayout>
  );
};

export default ServiceProviderOnboardingPage;
